import { AppConfig } from '@/utils/AppConfig';
import { useSearchParams } from 'next/navigation';
import { Fragment, useEffect } from 'react';
import { UseMenuProps, useMenu } from 'react-instantsearch';
import type { MenuItem } from 'instantsearch.js/es/connectors/menu/connectMenu';

interface ExtendedMenuItem extends MenuItem {
  oldLabel?: string;
}

export default function CustomMenu(props: UseMenuProps) {
  const router = useSearchParams();
  let { items, refine } = useMenu(props);

  useEffect(() => {
    const paramValue = router.get(props.attribute) || (props.attribute === 'categories' && router.get('category')) || '';
    if (paramValue) {
      refine(decodeURIComponent(paramValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let attributeName = props.attribute;
  switch (props.attribute) {
    case 'mode':
      attributeName = 'Modalité';
      items = items.map((item) => ({
        ...item,
        oldLabel: item.label,
        label: item.label
          .replace('mixed', 'Mixte')
          .replace('in_person', 'Présentielle')
          .replace('remote', 'Distancielle'),
      }));
      break;
    case 'theme':
      attributeName = 'Thème';
      break;
    case 'domain':
      attributeName = 'Tous';
      break;
    case 'postal_code':
    case 'postal_codes':
      attributeName = 'Code postal';
      break;
    case 'place':
    case 'places':
      attributeName = 'Lieu';
      break;
    case 'category':
      attributeName = 'Catégories';
      if (
        items.some((item) => ['Développement', 'Administratif', 'Technicien', 'Officiel'].includes(item.value))
      ) {
        attributeName = 'Famille';
      }
      break;
    case 'categories':
      attributeName = 'Catégories';
      break;
    case 'subcategories':
      attributeName = 'Sous-catégories';
      break;
    case 'type':
      attributeName = 'Type';
      items = items.map((item) => {
        return {
          ...item,
          oldLabel: item.label,
          label: item.label
            .replace(AppConfig.searchIndexNews, 'Articles')
            .replace(AppConfig.searchIndexVideos, 'Vidéos')
            .replace(AppConfig.searchIndexPhotos, 'Photos')
            .replace(AppConfig.searchIndexAssets, 'Documents'),
        };
      });
      items = items.filter((item) => item.value !== 'ffbbsite_pages');
      break;
    case 'ffbbsite_asset_type':
      attributeName = 'Type de ressource';
      break;
    case 'ffbbsite_asset_tag_parent':
      attributeName = 'Étiquette de ressource';
      break;
    case 'contract_type':
      attributeName = 'Type de contrat';
      break;
    case 'contract_duration':
      attributeName = 'Durée de contrat';
      break;
    case 'city':
      attributeName = 'Ville';
      break;
    case 'title':
      attributeName = 'Titre';
      break;
    case 'unit':
      attributeName = 'Pôle';
      break;
    case 'service':
      attributeName = 'Service';
      break;
    case 'job_start_filter':
      attributeName = 'Début de contrat';
      break;
    case 'degree':
      attributeName = 'Diplômes requis';
      break;
    case 'contract_specificities':
      attributeName = 'Spécificité du poste';
      break;
    default:
      attributeName = 'Filtre ' + props.attribute;
      break;
  }

  if (items.length === 0) return null;

  items.sort((a, b) => a?.label.localeCompare(b?.label));

  const selected = items.find((item) => item.isRefined);
  const value = selected ? selected.value : '';

  const MAX_LENGTH = 35;
  return (
    <select
      className="select select-bordered max-w-xs rounded-[100px] border-zinc-200 text-sm font-bold focus:outline-none"
      onChange={(e) => refine(e.target.value)}
      value={value}
      key={`${attributeName}-select`}
    >
      <option key="default" value="" className="capitalize">
        {attributeName}
      </option>
      {items.map((item, i) =>
        item.label.length > MAX_LENGTH ? (
          <Fragment key={i}>
            <option
              key={`${item.value}-hidden`}
              value={item.value}
              title={item.label}
              className="sm:hidden"
            >
              {`${item.label.substring(0, MAX_LENGTH)}...`}
            </option>
            <option key={`${item.value}-block`} value={item.value} className="hidden sm:block">
              {item.label}
            </option>
          </Fragment>
        ) : (
          <option key={`${item.value}-normal`} value={item.value}>
            {item.label}
          </option>
        ),
      )}
    </select>
  );
}
